<template>
  <div class="mt-10 pb-6" :class="showDivider && 'border-b border-gray-20'">
    <div class="relative">
      <div class="absolute left-3 top-1/2 z-10 flex -translate-y-1/2 items-center gap-3 border-r border-gray-30 pr-3">
        <ui-pill-toggle
          v-model="isAiMode"
          small
          :disabled="isSubmitting"
          :label="$t('conversations.filters.ai_search.ai_mode')"
        />
      </div>
      <input
        v-model.trim="inputValue"
        type="text"
        class="h-12 w-full rounded-lg border border-gray-30 bg-gray-10 pl-[8rem] text-sm text-gray-80 transition placeholder:text-gray-50 hover:border-gray-40 focus:border-zblue-60 focus:shadow-[inset_0px_0px_2px_rgba(59,76,191,0.6)] focus:outline-none"
        :class="isAiMode ? 'pr-24' : 'pr-4'"
        :placeholder="
          isAiMode ? $t('conversations.filters.ai_search.placeholder') : $t('filter.groups.search_placeholder')
        "
        @keydown.enter.prevent="isAiMode && submit()"
      />
      <div v-if="isAiMode" class="absolute right-3 top-1/2 flex -translate-y-1/2 items-center gap-3">
        <button
          v-if="inputValue"
          type="button"
          :aria-label="$t('conversations.filters.ai_search.clear')"
          class="flex h-6 w-6 items-center justify-center rounded-full text-gray-50 transition-colors hover:text-gray-70"
          @click="inputValue = ''"
        >
          <x-icon class="icon-s" />
        </button>
        <button
          type="button"
          :aria-label="$t('conversations.filters.ai_search.submit')"
          :disabled="submitDisabled"
          class="flex h-8 w-8 items-center justify-center rounded-full bg-zblue-60 text-white transition-colors hover:bg-zblue-70 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-zblue-60"
          @click="submit"
        >
          <search-icon class="icon-m" />
        </button>
      </div>
    </div>
    <ai-filter-options
      v-show="isAiMode"
      ref="aiFilterOptionsRef"
      :options="options"
      :autoqa-fields="autoqaFields"
      :spotlight-fields="spotlightFields"
      :intelligent-fields="intelligentFields"
      :prompt="inputValue"
      @ai-results="$emit('ai-results', $event)"
      @use-suggestion="onUseSuggestion"
      @loading="onAiLoading"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { UiPillToggle } from '@klausapp/ui-kit'
import { SearchIcon, XIcon } from '@klausapp/ui-kit/icons'

import { type DefaultFilterOption } from '../../api/filters'
import { type DeconstructedOption } from '../../utils/filters'
import AiFilterOptions from './AiFilterOptions.vue'

defineProps<{
  options: DefaultFilterOption[]
  autoqaFields: DefaultFilterOption[]
  spotlightFields: DefaultFilterOption[]
  intelligentFields: DefaultFilterOption[]
  showDivider?: boolean
}>()

const emit = defineEmits<{
  (e: 'ai-results', value: DeconstructedOption[]): void
  (e: 'update:search', value: string): void
  (e: 'loading', value: boolean): void
}>()

// Single source of truth for the mode lives in the parent (FilterModalV2)
const isAiMode = defineModel<boolean>('aiMode', { default: true })

const inputValue = ref('')
const aiFilterOptionsRef = ref<InstanceType<typeof AiFilterOptions> | null>(null)

const isSubmitting = ref(false)
const submittedValue = ref('')

// Re-enable submit once the input differs from the in-flight prompt, so a new search can start.
const submitDisabled = computed(
  () => !inputValue.value || (isSubmitting.value && inputValue.value === submittedValue.value),
)

const submit = () => {
  if (submitDisabled.value) return

  submittedValue.value = inputValue.value
  aiFilterOptionsRef.value?.submit()
}

const onAiLoading = (loading: boolean) => {
  isSubmitting.value = loading
  emit('loading', loading)
}

const onUseSuggestion = (suggestion: string) => {
  inputValue.value = suggestion
  submittedValue.value = suggestion
}

watch(isAiMode, () => {
  inputValue.value = ''
  submittedValue.value = ''
  isSubmitting.value = false
})

// In simple mode the input drives the condition search; in AI mode it holds the prompt
watch(inputValue, (val) => {
  emit('update:search', isAiMode.value ? '' : val)
})
</script>
